import parse from "html-react-parser";
import Cookies from "js-cookie";
import { useRouter } from "next/router";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Color from "../../../../assets/colors";
import { getCompanyLogoImage } from "../../../../helpers/data_management";
import { sendTrackingEvent } from "../../../../helpers/tracking_management";
import {
  bookmarkSingleJobCompany,
  unBookmarkSingleJobCompany,
} from "../../../../redux/actions/job_action";
import {
  updateShowSignInModalStatus,
  updateSignUpOrigin,
} from "../../../../redux/actions/navbar_action";
import { checkAuthentication } from "../../../../redux/actions/user_action";
import * as jobType from "../../../../redux/types/job_type";
import {
  CompanyNameRow,
  CompanyVerifiedBadge,
} from "../../AccordionJobCard/styles";
import {
  CompanyProfileBox,
  ContainedButton,
  GridStyled,
  ImageStyled,
  LinkStyled,
  OutlinedButton,
  SectionTitle,
  TypographyStyled,
} from "./styles";

function truncateDescription(description, maxLength = 420) {
  if (!description) return "";

  // Strip HTML tags and get plain text
  const plainText = description.replace(/<[^>]*>/g, "").trim();

  // Return if text is already shorter than max length
  if (plainText.length <= maxLength) return plainText;

  // Look for a space within the last 20 characters before maxLength
  let truncated = plainText.slice(0, maxLength + 1);
  let lastSpaceIndex = -1;

  // Search for the last space starting from maxLength backwards
  for (let i = maxLength; i >= maxLength - 20 && i >= 0; i--) {
    if (truncated[i] === " ") {
      lastSpaceIndex = i;
      break;
    }
  }

  // If no space found in last 20 chars, use the full length
  return lastSpaceIndex > 0
    ? plainText.slice(0, lastSpaceIndex) + "..."
    : plainText.slice(0, maxLength) + "...";
}

function SingleJobCompanyProfile(props) {
  const router = useRouter();
  const { t } = useTranslation();

  const { company } = props?.job || {};

  const companySizes = useSelector((state) => state?.companies?.companySizes);

  const isAggregatedJob = props?.job?.category === "aggregated";

  const companyLogo = useMemo(() => {
    return getCompanyLogoImage(company);
  }, [company]);

  const MemoTypography = React.memo(TypographyStyled);
  const MemoGrid = React.memo(GridStyled);
  const MemoCompanyContainer = React.memo(CompanyProfileBox);
  const MemoImageContainer = React.memo(ImageStyled);
  const MemoViewCompanyButton = React.memo(OutlinedButton);
  const MemoFollowButton = React.memo(FollowCompanyButton);
  const MemoNextLink = React.memo(LinkStyled);

  const [companySize, setCompanySize] = useState(false);

  function getCompanySize(id) {
    if (
      !id ||
      !Array.isArray(companySizes) ||
      (Array.isArray(companySizes) && companySizes.length < 1)
    )
      return "";

    let companySize = companySizes.find((companySize) => companySize.id == id)
      ? `• ${companySizes.find((companySize) => companySize.id == id).title}`
      : "";

    return companySize;
  }

  function triggerViewCompanyCE() {
    sendTrackingEvent({
      event: "CE_click-view-company-profile-ijp",
      "company-id": company.id,
      "job-id": props?.job.id,
    });

    Cookies.set("click-source", "job_detail_company_profile");
  }

  const companySizeString =
    getCompanySize(company.sizeId || company.size_id) ?? "";

  const isCompanyVerified =
    !isAggregatedJob &&
    (company?.verified === true ||
      company?.name === "Hiredly" ||
      company?.name === "Hiredly X");

  useEffect(() => {
    setCompanySize(true);
  }, []);

  return (
    <Fragment>
      <SectionTitle variant="h1">
        {t("jobs.company_profile.title")}
      </SectionTitle>
      <MemoCompanyContainer marginBottom={company?.hide ? 0 : "30px"}>
        <MemoGrid container marginBottom={"16px"} columns={12} rowGap={"15px"}>
          <MemoGrid
            container
            item
            xs={12}
            tablet={9}
            alignItems={"center"}
            flexWrap={"nowrap"}
          >
            <MemoImageContainer
              src={companyLogo}
              width={70}
              height={70}
              aria-label="company-logo"
              alt={`${company.name}-logo-image`}
            />
            <div>
              <CompanyNameRow
                sx={{
                  minWidth: 0,
                  maxWidth: "100%",
                  alignItems: "flex-start",
                  marginBottom: "10px",
                }}
              >
                <MemoTypography
                  variant="h2"
                  fontSize={19}
                  fontWeight={"bold"}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: "-webkit-box",
                    WebkitLineClamp: "2",
                    WebkitBoxOrient: "vertical",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                    whiteSpace: "wrap",
                    overflow: "hidden",
                  }}
                  aria-label="company-name"
                >
                  {isAggregatedJob
                    ? props?.job?.aggregatedCompanyName
                    : company.name}
                </MemoTypography>
                {isCompanyVerified && (
                  <CompanyVerifiedBadge
                    aria-label="Verified company"
                    style={{ width: "25px", height: "25px", fontSize: "25px" }}
                  />
                )}
              </CompanyNameRow>
              {companySize === true && companySizeString ? (
                <MemoTypography
                  variant="h3"
                  fontSize={"16px"}
                  aria-label="company-industry"
                >
                  {company.industry} {companySizeString}
                </MemoTypography>
              ) : null}
            </div>
          </MemoGrid>
          <MemoGrid
            container
            item
            xs={12}
            tablet={3}
            alignItems={"center"}
            justifyContent={"flex-end"}
          >
            <MemoFollowButton />
          </MemoGrid>
        </MemoGrid>
        {company?.profile?.descriptions ? (
          <MemoGrid marginBottom={"15px"}>
            {company?.profile?.descriptions
              ?.slice(0, 1)
              ?.map((description, index) => (
                <div key={`company-description-${index}`}>
                  {parse(truncateDescription(description.body), { trim: true })}
                </div>
              ))}
          </MemoGrid>
        ) : null}
        {company?.hide ? null : (
          <MemoGrid container justifyContent={"center"}>
            <MemoNextLink
              href={`/companies/${company.slug}`}
              onClick={triggerViewCompanyCE}
            >
              <MemoViewCompanyButton variant="outlined">
                {t("jobs.company_profile.view_profile")}
              </MemoViewCompanyButton>
            </MemoNextLink>
          </MemoGrid>
        )}
      </MemoCompanyContainer>
    </Fragment>
  );
}

function FollowCompanyButton() {
  const [isLoading, setIsLoading] = useState(false);
  const reduxJobCompany = useSelector((state) => state?.jobs?.job?.company);
  const { bookmark, id } = reduxJobCompany || {};
  const dispatch = useDispatch();
  const { t } = useTranslation();

  async function handleFollowCompany() {
    setIsLoading(true);
    const loggedIn = checkAuthentication();

    if (!loggedIn) {
      setIsLoading(false);

      dispatch(updateSelectedCompany(company)).then(() => {
        dispatch(updateSignUpOrigin("save_company")).then(() => {
          dispatch(updateShowSignInModalStatus(true));
        });
      });
    }

    if (bookmark) {
      const unbookmarkRes = await dispatch(
        unBookmarkSingleJobCompany({ companyId: id })
      );

      if (unbookmarkRes.type === jobType.FOLLOW_SINGLE_JOB_COMPANY_SUCCEED) {
        sendTrackingEvent({
          event: "CE_unfollow-company-ijp",
          "company-id": id,
        });
      }

      setIsLoading(false);
    } else if (!bookmark) {
      const bookmarkRes = await dispatch(
        bookmarkSingleJobCompany({ companyId: id })
      );

      if (bookmarkRes.type === jobType.FOLLOW_SINGLE_JOB_COMPANY_SUCCEED) {
        sendTrackingEvent({
          event: "CE_follow-company-ijp",
          "company-id": id,
        });
      }

      setIsLoading(false);
    }
  }

  return (
    <ContainedButton
      variant="contained"
      onClick={handleFollowCompany}
      loading={isLoading}
      disabled={isLoading}
      sx={{
        backgroundColor: bookmark && Color.grey,
        color: bookmark && Color.black,
      }}
    >
      {bookmark === true
        ? t("jobs.company_profile.unfollow")
        : t("jobs.company_profile.follow")}
    </ContainedButton>
  );
}

export default SingleJobCompanyProfile;
